import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "wouter";
import { useProfile } from "@/lib/api";
import { useOnboardingAutosave, useOnboardingSave } from "@/lib/onboardingSave";
import { useAuth } from "@/lib/platform/AuthProvider";
import { useT } from "@/lib/i18n";
import tracking from "@/lib/tracking";
import { getQuizAudience } from "@/lib/quizAudience";
import { buildOnboardingContext } from "../components/onboarding/trackingContext";
import OnboardingFlow from "../components/onboarding/OnboardingFlow";
import type { OnboardingFlowConfig } from "../components/onboarding/flow";
import { Loader } from "@/components/Loader";

/**
 * Host page for the v3 onboarding flow.
 *
 * Owns state and persistence only — the screens, their order, and the copy all
 * live in `components/onboarding`. This split is what lets `MainApp` swap the
 * entire flow for another page component, and lets an experiment vary the
 * screen list without a page of its own.
 *
 * Answer plumbing deliberately mirrors `OnboardingQuiz`: hydrate from profile
 * + localStorage draft, persist every change to the draft, and hand the save
 * off to `/quiz?saveAnswers` so both flows write through exactly one code
 * path. Draft compatibility also means a user re-bucketed mid-flow keeps
 * everything they've already answered.
 */

/** Variants of the flow itself. Add arms here as experiments are designed. */
const FLOW_CONFIGS: Record<string, OnboardingFlowConfig | undefined> = {
  v3: undefined,
};

interface OnboardingProps {
  /** Which arm of the flow to render. Reported on every tracking event. */
  variant?: string;
}

export default function Onboarding({ variant = "v3" }: OnboardingProps) {
  const { t, language } = useT();
  const { isLoaded: isUserLoaded } = useAuth();
  const [searchParams] = useSearchParams();
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [isInitialized, setIsInitialized] = useState(false);

  const { data: profileData, isLoading: isLoadingProfile } = useProfile();
  // Owns the save, and resumes one that was interrupted.
  const { finish: finishOnboarding, leave: leaveOnboarding, isSaving } = useOnboardingSave();
  // Once there's an account, answers go through as the flow moves rather than
  // waiting for the last screen — the flow signs people in part way, so
  // everything after that used to live only in this browser. The flow mirrors
  // its current screen into `?step=`, which is where the trigger comes from:
  // one write per screen left behind, never mid-question.
  useOnboardingAutosave(answers, searchParams.get("step"));

  const config = useMemo(() => FLOW_CONFIGS[variant], [variant]);

  // Hydrate: saved profile first, local draft on top (a draft is always the
  // more recent intent).
  useEffect(() => {
    if (isLoadingProfile || isInitialized || !isUserLoaded) return;

    tracking.quizStarted();

    let initial: Record<string, any> = {};
    if (profileData?.success && profileData?.data?.profile) {
      const profile = profileData.data.profile as Record<string, any>;
      initial = {
        location: profile.location,
        genderIdentity: profile.genderIdentity,
        pronouns: profile.pronouns,
        sexualOrientation: profile.sexualOrientation,
        birthday: profile.birthday,
        community: profile.community,
        ageRangePreferences: profile.ageRangePreferences,
        dinnerVibe: profile.dinnerVibe,
        conversationTopics: profile.conversationTopics,
        groupRole: profile.groupRole,
        connectionIntent: profile.connectionIntent,
        meetingChannels: profile.meetingChannels,
      };
      Object.keys(initial).forEach((key) => {
        if (initial[key] === undefined || initial[key] === null) delete initial[key];
      });
    }

    const draft = localStorage.getItem("quizAnswers");
    if (draft) {
      try {
        initial = { ...initial, ...JSON.parse(draft) };
      } catch {
        // Corrupt draft — start from the profile rather than blocking the flow.
      }
    }

    if (Object.keys(initial).length > 0) setAnswers(initial);
    setIsInitialized(true);
  }, [profileData, isLoadingProfile, isInitialized, isUserLoaded]);

  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      localStorage.setItem("quizAnswers", JSON.stringify(answers));
    }
  }, [answers]);

  const handleAnswer = useCallback((field: string, value: any) => {
    setAnswers((prev) => ({ ...prev, [field]: value }));
  }, []);

  /**
   * Finish. Saves in place rather than navigating to `/quiz?saveAnswers` and
   * letting that page do it — see `lib/onboardingSave`. The hook also resumes
   * a save the flow didn't get to finish, which is what makes this survive a
   * sign-in that reloads the page.
   */
  const saveAndExit = useCallback((navOverride?: string) => {
    tracking.onboardingFlowCompleted(
      buildOnboardingContext({
        variant,
        audience: getQuizAudience(),
        language,
        answers,
        // Reaching the end means an account exists — the flow cannot finish
        // without one. Stated rather than read so the last event in the funnel
        // matches the steps after the gate.
        isSignedIn: true,
      }),
    );
    // The flow names its own destination rather than taking the generic
    // default, because it ends by promising a first night rather than a home
    // screen, and the dashboard does not keep that promise: no upcoming
    // events, no way to book one.
    //
    // This is not the same as defaulting `nav` to "booking" globally. An
    // incoming `nav` still wins (the event page someone was on when the gate
    // opened), and "booking" still counts as generic to
    // `resolvePostQuizDestination`, so ad traffic in the guided experiment is
    // upgraded to /booking/start. Surfaces that end without a promise — the
    // stepper's save — keep passing nothing and keep landing on the dashboard.
    // Where they land depends on what they finished with.
    //
    // A seat booked inside the flow (see PlansScreen) leaves the booking page
    // nothing to ask, so it would only be a screen to close — the dashboard is
    // where the booking they just made lives. That beats a generic incoming
    // `nav` too: "booking" was a sensible default for someone who had not
    // booked, and is a wrong turn for someone who has.
    //
    // Otherwise, a night already chosen means "booking" is *that* night, and
    // a bare /booking would ask them to find it again.
    const eventId = answers.selectedEventId as string | undefined;
    const incoming = searchParams.get("nav");
    const incomingIsGeneric =
      !incoming || incoming === "booking" || incoming === "dashboard";
    const destination = navOverride
      ? navOverride
      : answers.reservedBookingId
      ? incomingIsGeneric
        ? "dashboard"
        : incoming
      : (incoming ??
        (eventId ? `booking?eventId=${encodeURIComponent(eventId)}` : "booking"));
    finishOnboarding(destination, Boolean(answers.reservedBookingId));
  }, [finishOnboarding, searchParams, variant, language, answers]);

  /**
   * An escape hatch. Same save as finishing, but not a completion: the flow
   * was left, not finished, and the hatch has already reported itself. The
   * path is used as given — see `OnboardingRuntime.leave`.
   */
  const leaveEarly = useCallback(
    (path: string) => {
      leaveOnboarding(path, Boolean(answers.reservedBookingId));
    },
    [leaveOnboarding, answers.reservedBookingId],
  );

  if (isSaving) {
    return (
      <div className="flex items-center justify-center bg-dark-bg min-h-[60vh]">
        <div className="text-center">
          <Loader className="mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t("quiz.saving")}</h2>
          <p className="text-dark-muted">{t("quiz.justMoment")}</p>
        </div>
      </div>
    );
  }

  if (isLoadingProfile || !isInitialized) {
    return (
      <div className="flex items-center justify-center bg-dark-bg min-h-[60vh]">
        <div className="text-center">
          <Loader className="mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t("common.messages.loading")}</h2>
          <p className="text-dark-muted">{t("quiz.justMoment")}</p>
        </div>
      </div>
    );
  }

  return (
    // Deliberately not wrapped in `BookingProvider`. It owns `/booking`'s URL:
    // `setSelectedEventId` navigates there on every pick, and it redirects
    // there on mount for anyone holding a booking already. Onboarding reuses
    // the logic underneath it instead — same hooks, same rules, no page.
    <OnboardingFlow
      variant={variant}
      config={config}
      answers={answers}
      onAnswer={handleAnswer}
      onFinish={saveAndExit}
      onLeave={leaveEarly}
    />
  );
}
